/**
 * Today's log (G8) — stats + the full entry list with status filters.
 * Wave D: guards can REPORT a mistaken entry (wrong flat, wrong name) — the
 * record stays but wears the report, and admins get pinged to review.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space, type PillStatus } from '@bero/tokens';
import { str, useGateList } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT, monthShort } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, Chip, EmptyState, Field, Input, Pill, Screen,
  SectionLabel, Sheet, Skeleton, TopBar,
} from '@/ui';

type Row = {
  id: string; name: string; type: string; flat: string; status: string;
  when: string; reportedReason: string | null;
};

function toRow(v: unknown, i: number): Row {
  const iso = str(v, 'arrivedAt', 'createdAt');
  const d = iso ? new Date(iso) : null;
  let when = '';
  if (d && !Number.isNaN(d.getTime())) {
    let h = d.getHours(); const am = h < 12 ? 'AM' : 'PM'; h = h % 12 || 12;
    when = `${d.getDate()} ${monthShort(d.getMonth())} · ${h}:${String(d.getMinutes()).padStart(2, '0')} ${am}`;
  }
  return {
    id: str(v, 'id') ?? `r${i}`,
    name: str(v, 'name') ?? 'Visitor',
    type: (str(v, 'type') ?? '').toLowerCase(),
    flat: str(v, 'flatCode') ?? '',
    status: (str(v, 'status') ?? 'PENDING').toUpperCase(),
    when,
    reportedReason: str(v, 'reportedReason') ?? null,
  };
}

const PILL: Record<string, { status: PillStatus; label: DictKey }> = {
  PENDING: { status: 'pending', label: 'pill_awaiting' },
  APPROVED: { status: 'approved', label: 'pill_approved' },
  REJECTED: { status: 'rejected', label: 'pill_rejected' },
  LEFT_AT_GATE: { status: 'open', label: 'pill_at_gate' },
};

const FILTERS = ['All', 'Approved', 'Rejected', 'Awaiting'];
const FILTER_STATUS: Record<string, string | null> = {
  All: null, Approved: 'APPROVED', Rejected: 'REJECTED', Awaiting: 'PENDING',
};
const FILTER_LABEL: Record<string, DictKey> = {
  All: 'filter_all', Approved: 'filter_approved', Rejected: 'filter_rejected', Awaiting: 'filter_awaiting',
};

export default function GuardLog() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state, reload } = useGateList<unknown>('/api/gate/feed', 'items');
  const rows = items.map(toRow);
  const [filter, setFilter] = React.useState('All');
  const [reportFor, setReportFor] = React.useState<Row | null>(null);
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);
  const want = FILTER_STATUS[filter];
  const visible = want ? rows.filter((r) => r.status === want) : rows;

  const submitReport = () => {
    if (!reportFor || busy) return;
    if (reason.trim().length < 3) { setNotice(t('log_report_need_reason')); return; }
    setBusy(true);
    api.post<unknown>(`/api/gate/entries/${reportFor.id}/report`, { reason: reason.trim() })
      .then(() => { setNotice(t('log_reported_ok')); void reload(); })
      .catch((e) => setNotice((e as { error?: string })?.error ?? t('log_report_error')))
      .finally(() => { setBusy(false); setReportFor(null); setReason(''); });
  };

  return (
    <Screen scroll={false}>
      <TopBar title={t('log_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2, marginBottom: space.s4 }}>
          {FILTERS.map((f) => (
            <Chip key={f} label={t(FILTER_LABEL[f])} active={filter === f} onPress={() => setFilter(f)} />
          ))}
        </View>
        {notice ? (
          <AppText variant="bodySm" weight="500" style={{ marginBottom: space.s3, color: colors.textPrimary }}>
            {notice}
          </AppText>
        ) : null}
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} h={68} />)}</View>
        ) : visible.length === 0 ? (
          <EmptyState
            icon={<Icon name="list" size={40} color={colors.textTertiary} />}
            title={t('log_empty_title')}
            body={t('log_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {visible.map((r) => {
              const p = PILL[r.status] ?? PILL.PENDING;
              return (
                <Card key={r.id}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="600">{r.name}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                        {[r.type, r.flat, r.when].filter(Boolean).join(' · ')}
                      </AppText>
                    </View>
                    {r.reportedReason ? <Pill status="urgent" label={t('pill_reported')} /> : null}
                    <Pill status={p.status} label={t(p.label)} />
                  </View>
                  {r.reportedReason ? (
                    <AppText variant="bodySm" style={{ marginTop: space.s2, color: colors.accent700 }}>
                      {r.reportedReason}
                    </AppText>
                  ) : (
                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', marginTop: space.s2 }}>
                      <AppText
                        variant="bodySm" weight="500" tone="tertiary"
                        onPress={() => { setReportFor(r); setReason(''); setNotice(null); }}
                      >
                        {t('log_report_link')}
                      </AppText>
                    </View>
                  )}
                </Card>
              );
            })}
          </View>
        )}
      </Screen>

      <Sheet visible={reportFor !== null} onClose={() => setReportFor(null)}>
        {reportFor ? (
          <>
            <SectionLabel>{t('log_report_hdr')}</SectionLabel>
            <AppText variant="headingSm" weight="500" style={{ marginTop: space.s2 }}>
              {reportFor.name}{reportFor.flat ? ` · ${reportFor.flat}` : ''}
            </AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
              {t('log_report_explain')}
            </AppText>
            <View style={{ marginTop: space.s4 }}>
              <Field label={t('log_report_field')}>
                <Input value={reason} onChangeText={setReason} multiline maxLength={250}
                  placeholder={t('log_report_placeholder')} />
              </Field>
            </View>
            <Button title={busy ? t('log_reporting') : t('log_report_submit')} disabled={busy}
              style={{ marginTop: space.s4 }} onPress={submitReport} />
          </>
        ) : null}
      </Sheet>
    </Screen>
  );
}
